import { Component } from 'preact';
import { Link } from 'preact-router';

import handleLoadingAndError from '../handle-loading-and-error';
import sizeAware from '../size-aware';
import Selector from '../selector';
import {
	formatPressure,
	formatRainfall,
	formatRainRate,
	formatTemperature,
	formatTemperatureInterval,
	formatWindGust,
	formatWindSpeed,
	getDateTimeFormatConverter
} from '../../utils/formatters';
import style from './style.scss';
import { getParametersData } from './parameters-data';

/**
 * The RecordsTopTen component is a view available for display on the History Records page.
 * It displays a list of parameters to choose from, and will display a table of the top
 * 10 records for that parameter ranked from 1st to 10th.
 *
 * props:
 * - data: full top10 records data set
 */
class RecordsTopTen extends Component {

	constructor(props, context) {
		super(props, context);

		const { units } = context.userSettings;

		this.formatters = {
			date: getDateTimeFormatConverter('%Y-%m-%d', '%-d %b %Y'),
			time: getDateTimeFormatConverter('%H:%M:%S', '%-I:%M %p'),
			temperature: t => formatTemperature(t, units.temperature),
			temperatureInterval: t => formatTemperatureInterval(t, units.temperature) + '°' + units.temperature,
			rainfall: r => formatRainfall(r, units.rain),
			rainRate: r => formatRainRate(r, units.rain),
			pressure: p => formatPressure(p, units.pressure),
			windGust: w => formatWindGust(w, units.speed),
			windRun: w => formatWindSpeed(w, units.speed)
		};

		this.PARAMETER_ID = {
			HIGHEST_HIGH_TEMPERATURE: 'highest_HighTemperature',
			LOWEST_HIGH_TEMPERATURE: 'lowest_HighTemperature',
			LOWEST_LOW_TEMPERATURE: 'lowest_LowTemperature',
			HIGHEST_LOW_TEMPERATURE: 'highest_LowTemperature',
			HIGHEST_MEAN_TEMPERATURE: 'highest_MeanTemperature',
			LOWEST_MEAN_TEMPERATURE: 'lowest_MeanTemperature',
			LARGEST_TEMPERATURE_RANGE: 'largest_TemperatureRange',
			SMALLEST_TEMPERATURE_RANGE: 'smallest_TemperatureRange',
			HIGHEST_RAINFALL: 'highest_Rainfall',
			HIGHEST_HOURLY_RAINFALL: 'highest_HighHourlyRain',
			HIGHEST_RAIN_RATE: 'highest_HighRainRate',
			HIGHEST_WIND_GUST: 'highest_HighWindGust',
			HIGHEST_WIND_RUN: 'highest_WindRun',
			HIGHEST_PRESSURE: 'highest_HighPressure',
			LOWEST_PRESSURE: 'lowest_LowPressure',
			HIGHEST_DEWPOINT: 'highest_HighDewpoint',
			LOWEST_DEWPOINT: 'lowest_LowDewpoint',
			LOWEST_HUMIDITY: 'lowest_LowHumidity'
		};

		this.state = {
			activeParameterId: this.PARAMETER_ID.HIGHEST_HIGH_TEMPERATURE
		};

		this.parameters = getParametersData(this.PARAMETER_ID, units, this.formatters);

		this.onParameterListClick = this.onParameterListClick.bind(this);
		this.onParameterListKeyPress = this.onParameterListKeyPress.bind(this);
		this.onOptionSelect = this.onOptionSelect.bind(this);
	}

	changeParameter(id) {
		this.setState({ activeParameterId: id });
	}

	onParameterListClick(evt) {
		if (evt.target.hasAttribute('data-id')) {
			const id = evt.target.getAttribute('data-id');

			if (id !== this.state.activeParameterId) {
				this.changeParameter(id);
			}
		}
	}

	onParameterListKeyPress(evt) {
		if (evt.keyCode === 13) {
			this.onParameterListClick(evt);
		}
	}

	onOptionSelect(opt) {
		this.changeParameter(opt.value);
	}

	renderParametersList() {
		return (
			<ul class={style.parameters_list} onClick={this.onParameterListClick} onKeyPress={this.onParameterListKeyPress}>
				{
					this.parameters.map(param => {
						const cssClass = param.id === this.state.activeParameterId
							? style.active
							: null;

						return (
							<li tabIndex="0" class={cssClass} key={param.id} data-id={param.id}>{ param.label }</li>
						);
					})
				}
			</ul>
		);
	}

	renderSelector() {
		const options = this.parameters.map(param => ({ value: param.id, label: param.label }));

		return (
			<Selector options={options}
			          selectedValue={this.state.activeParameterId}
			          onOptionSelect={this.onOptionSelect} />

		);
	}

	renderTable(data, paramData) {

		const extraColumns = Array.isArray(paramData.additionalColumns) ? paramData.additionalColumns : [];

		return (
			<div role="table" class={style.table}>
				<div role="rowgroup">
					<div role="row">
						<div role="columnheader">Rank</div>
						<div role="columnheader">Value ({ paramData.unit })</div>
						{
							extraColumns.map(column => (
								<div role="columnheader">{ column.label }</div>
							))
						}
						<div role="columnheader">Date</div>
					</div>
				</div>
				<div role="rowgroup">
					{
						data.map((d, i) => {
							const value = d[paramData.field];
							const formattedValue = paramData.formatter(value);
							return (
								<div key={i} role="row">
									<div role="cell">{ i + 1 }</div>
									<div role="cell">{ formattedValue }</div>
									{
										extraColumns.map(column => {
											const columnValue = d[column.field];
											const formattedColumnValue = column.formatter(columnValue);
											return <div role="cell">{ formattedColumnValue }</div>;
										})
									}
									<div role="cell">
										<Link href={`/history-daily/${d.date}`}>{ this.formatters.date(d.date) }</Link>
									</div>
								</div>
							);
						})
					}
				</div>
			</div>
		);
	}

	render(props) {
		const paramData = this.parameters.filter(p => p.id === this.state.activeParameterId)[0];
		const data = this.props.data[this.state.activeParameterId];

		return (
			<div class={style.records_top_ten}>

				<div class={style.parameters_wrapper}>
					{
						props.windowWidth < 600
							? this.renderSelector()
							: this.renderParametersList()
					}
				</div>

				<div class={style.table_wrapper}>
					<h3 class={style.table_title}>{ paramData.label }</h3>
					<p class={style.table_description}>{ paramData.description }</p>
					{ this.renderTable(data, paramData) }
				</div>

			</div>
		);
	}

}

export default handleLoadingAndError(sizeAware(RecordsTopTen));



// WEBPACK FOOTER //
// ./components/records-top-ten/index.js